import classnames from 'classnames';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { makePaginated } from '../../../../../adl-gen/common';
import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import { makeUserGroup, UserGroup } from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { UserGroupListEntry } from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { GroupData } from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { newTextField } from '../../../../../ui/widgets/text-input/text-field';
import { uniqueId } from '../../../../../util/utils';
import { GroupUserChange, GroupUserQuizChange, UserGroupStore } from '../../../../stores';
import { CheckItemObserver, deleteHandler, renderButtons, TextInputObserver } from '../../../common/editor';
import { AdminApplicationRoleType } from '../../../common/list/list-types';
import { ListView } from '../../../common/list/list-view';
import {
  GroupAppUserListEntry,
  GroupAppUserListView,
  GroupAppUsers,
  UnassignedAppUserListView
} from '../../lists/appuser-list/group-appuser-list-view';
import {
  QuizListEntry,
  UserGroupListEntryWithQuizChanges,
  UserGroupQuizListView
} from '../../lists/quiz-list/quiz-list-view';
import { UserQuizListSummaryView } from '../../lists/quiz-list/user-quiz-list-view';

const styles = require('../../../common/editor.css');
// const buttonStyles = require("../../common/common.css");

interface UserGroupEditorProps {
  debug?: boolean;
  item?: UserGroupListEntry;
  data: GroupData;
  store: UserGroupStore;
  onCreate(item: UserGroup, groupUpdates: GroupUserChange[], userQuizUpdates: GroupUserQuizChange[]): Promise<void>;
  onUpdate?(
    item: WithDbId<UserGroup>,
    groupUpdates: GroupUserChange[],
    userQuizUpdates: GroupUserQuizChange[]
  ): Promise<void>;
  onCancel(): void;
  applicationRole: AdminApplicationRoleType;
}

/**
 * Component to maintain Groups.
 */

/// --- [ TODO Global UserGroup; Other UserGroups; Manager Y/N ] ---
@observer
export class UserGroupEditor extends React.Component<UserGroupEditorProps> {
  @observable fieldState = {
    name: newTextField(),
    isGlobal: false,

    quizzes: Array<GroupUserQuizChange>(),

    assignedUsers: Array<GroupUserChange>()
  };

  @observable locked = false;

  // TODO: replace switcheroo with actual delete from ListView observable
  @observable usersTabKey: React.ReactText = 0;
  @observable assessmentsTabKey: React.ReactText = 0;

  constructor(props: UserGroupEditorProps) {
    super(props);
    const state = this.fieldState;
    if (props.item) {
      state.name.onChange(props.item.name);
      state.isGlobal = props.item.isGlobal;

      state.quizzes = props.item.quizzes.map(uq => ({
        kind: 'loaded',
        value: uq
      }));
      // Groups: Set up managers first
      for (const manager of props.item.managers) {
        const user = props.data.userList.find(u => u.id === manager.value.appUserId);
        if (user) {
          state.assignedUsers.push({
            kind: 'loaded',
            userId: user.id,
            manager: manager.id,
            groupId: manager.value.userGroupId
          });
        }
      }
      // Then update/insert for members
      for (const member of props.item.members) {
        const user = props.data.userList.find(u => u.id === member.value.appUserId);
        if (user) {
          const existingIdx = state.assignedUsers.findIndex(au => au.userId === user.id);
          if (existingIdx >= 0) {
            const existing = state.assignedUsers[existingIdx];
            existing.member = member.id;
            state.assignedUsers.splice(existingIdx, 1, existing);
          } else {
            state.assignedUsers.push({
              kind: 'loaded',
              userId: user.id,
              member: member.id,
              groupId: member.value.userGroupId
            });
          }
        }
      }
    }
  }

  @action
  onChangeUsersTab = (nextKey: React.ReactText) => {
    this.usersTabKey = nextKey;
  };

  @action
  onChangeAssessmentsTab = (nextKey: React.ReactText) => {
    this.assessmentsTabKey = nextKey;
  };

  @action lockUpdates = () => (this.locked = true);

  @action unlockUpdates = () => (this.locked = false);

  @action
  toggleGlobal = () => {
    this.fieldState.isGlobal = !this.fieldState.isGlobal;
  };

  NameObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.name,
      type: 'text',
      title: 'Group Name',
      placeholder: '',
      xsSpan: 2
    })
  );

  GlobalObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.isGlobal,
      label: 'Global group',
      xsSpan: 2,
      onChange: this.toggleGlobal,
      disabled: this.props.applicationRole === 'client-admin'
    })
  );

  AssignedUserListObserver = observer(() => {
    // Don't display for 'new' - we need a Group ID and don't want to orchestrate that rn
    if (!this.props.item) {
      return null;
    }
    return (this.assignedUserList.length > 0 ?
      <React.Fragment>
        <h5>Assigned Users</h5>
        <ListView
          // Trigger re-render on change
          key={`${this.assignedUserList.length}`}
          list={makePaginated({
            items: this.assignedUserList,
            current_offset: 0,
            total_size: this.assignedUserList.length
          })}
          fields={GroupAppUserListView}
          refData={this.groupAppUsers}
          onToggle={this.onChangeGroupUserAssignment}
        />
      </React.Fragment>
      : null
    );
  });

  UnassignedUserListObserver = observer(() => {
    return (
      <React.Fragment>
          <h5>Unassigned Users</h5>
          <ListView
            // Trigger re-render on change
            key={`${this.unassignedUserList.length}`}
            list={makePaginated({
              items: this.unassignedUserList,
              current_offset: 0,
              total_size: this.unassignedUserList.length
            })}
            fields={UnassignedAppUserListView}
            onToggle={this.onChangeGroupUserAssignment}
          />
      </React.Fragment>
    );
  });

  QuizListObserver = observer(() => {
    const { item } = this.props;
    const refData: UserGroupListEntryWithQuizChanges = item
      ? { ...item, updates: this.fieldState.quizzes }
      : { updates: this.fieldState.quizzes };
    return (
      <React.Fragment>
        <h5>Available Assessments</h5>
        <ListView
          key={`${this.fieldState.quizzes.length}`} 
          list={makePaginated({
            items: this.quizList,
            current_offset: 0,
            total_size: this.quizList.length
          })}
          fields={UserGroupQuizListView}
          refData={refData}
          onToggle={this.onCreateUserQuiz}
        />
      </React.Fragment>
    );
  });

  UserQuizListSummaryObserver = observer(() => {
    const { item } = this.props;
    const refData: UserGroupListEntryWithQuizChanges = item
      ? { ...item, updates: this.fieldState.quizzes }
      : { updates: this.fieldState.quizzes };
    return (
      <>
        <h5>Allocated Assessments</h5>
        <ListView
          key={`${this.fieldState.quizzes.length}`} 
          list={makePaginated({
            items: this.quizList,
            current_offset: 0,
            total_size: this.quizList.length
          })} 
          fields={UserQuizListSummaryView} 
          refData={refData} 
        />
      </>
    );
  });

  @computed
  get assignedUserList(): GroupAppUserListEntry[] {
    const { applicationRole, data } = this.props;
    const items = data.userList
      .filter(user => {
        const record = this.fieldState.assignedUsers.find(
          userChangeRecord => userChangeRecord.userId === user.id);
        if (record) {
          return !!record.member || !!record.manager;
        }
        return false;
      })
      .map(userWithId => ({
        id: userWithId.id,
        ...userWithId.value,
        identity: applicationRole
      }));
    return items;
  }

  @computed
  get unassignedUserList(): GroupAppUserListEntry[] {
    const { applicationRole, data } = this.props;
    const items = data.userList
      .filter(user => {
        const record = this.fieldState.assignedUsers.find(
          userChangeRecord => userChangeRecord.userId === user.id);
        if (record) {
          return record.member === undefined && record.manager === undefined;
        }
        return true;
      })
      .map(userWithId => ({
        id: userWithId.id,
        ...userWithId.value,
        identity: applicationRole
      }));
    return items;
  }

  @computed
  get groupAppUsers(): GroupAppUsers {
    return {
      userGroup: this.props.item,
      members: this.fieldState.assignedUsers.filter(u => u.member).map(u => u.userId),
      managers: this.fieldState.assignedUsers.filter(u => u.manager).map(u => u.userId)
    };
  }

  @computed
  get quizList(): QuizListEntry[] {
    const { data: userData } = this.props;
    return userData.quizList
        .filter(item => {
          return userData.appClient.value.displayClientAssessmentsOnly
            ? item.value.workRole.owner.kind === 'appClientId' &&
                item.value.workRole.owner.value === userData.appClient.id
            : true;
        })
        .map(qWithId => ({
          id: qWithId.id,
          imageUrl: qWithId.value.imageUrl,
          title: qWithId.value.title.value.en,
          workRoleId: qWithId.value.workRoleId,
          workRole: qWithId.value.workRole,
          lockResults: qWithId.value.lockResults
        }));
  }

  @action
  onChangeGroupUserAssignment = (fieldName: string, item: GroupAppUserListEntry, value: boolean): void => {

    const updatedUsers = value ? 
      this.onAddMemberOrManagerToGroup(fieldName, item) :
      this.onRemoveMemberOrManagerFromGroup(fieldName, item);
    
    this.fieldState.assignedUsers = updatedUsers;
  }

  onAddMemberOrManagerToGroup = (fieldName: string, item: GroupAppUserListEntry): GroupUserChange[] => {
    const assignedUsers = this.fieldState.assignedUsers.slice();
    // Check if unassigned
    const existingIdx = assignedUsers.findIndex(u => u.userId === item.id);
    if (existingIdx < 0) {
      // add
      assignedUsers.push({
        kind: 'create',
        userId: item.id,
        member: fieldName === 'member' ? `${uniqueId()}` : undefined,
        manager: fieldName === 'manager' ? `${uniqueId()}` : undefined
      });
    } else {
      const existing = assignedUsers[existingIdx];
      const update: GroupUserChange = { 
        ...existing,
        kind: existing.kind === 'loaded' ? 'update' : existing.kind,
      };
      if (fieldName === 'member') {
        update.member = existing.member || `${uniqueId()}`;
      }
      if (fieldName === 'manager') {
        update.manager = existing.manager || `${uniqueId()}`;
      }
      // update
      assignedUsers.splice(existingIdx, 1, update);
    }
    return assignedUsers;
  }

  onRemoveMemberOrManagerFromGroup = (fieldName: string, item: GroupAppUserListEntry): GroupUserChange[] => {
    const assignedUsers = this.fieldState.assignedUsers.slice();
    // Check if unassigned
    const existingIdx = assignedUsers.findIndex(u => u.userId === item.id);
    if (existingIdx >= 0) {
      const existing = assignedUsers[existingIdx];
      const update: GroupUserChange = { 
        ...existing,
        kind: existing.kind === 'loaded' ? 'update' : existing.kind,
      };
      if (fieldName === 'member') {
        update.member = undefined;
      }
      if (fieldName === 'manager') {
        update.manager = undefined;
      }
      // update
      assignedUsers.splice(existingIdx, 1, update);
    }
    return assignedUsers;
  }

  @action
  onCreateUserQuiz = (fieldName: string, item: QuizListEntry): void => {
    // Currently only supporting "create"
    if (fieldName === 'create') {
      /// New state not mutated state
      const quizzes = this.fieldState.quizzes.slice();
      /// Find all users who currently haven't got a non-started quiz for this group
      const members = this.fieldState.assignedUsers.filter(u => u.member).map(u => u.userId);
      const emptyQuizzes = quizzes.reduce((acc: string[], next) => {
        if (next.kind === 'loaded' || next.kind === 'update') {
          if (next.value.value.quizId === item.id && next.value.value.startedAt === null) {
            // unique list
            return acc.includes(next.value.value.appUserId) ? acc : [...acc, next.value.value.appUserId];
          }
        } else if (next.kind === 'create' && next.value === item.id && next.userId) {
          // unique list
          return acc.includes(next.userId) ? acc : [...acc, next.userId];
        }
        return acc;
      }, []);
      for (const member of members) {
        if (!emptyQuizzes.includes(member)) {
          /// Inject create into array
          quizzes.push({ kind: 'create', value: item.id, userId: member });
        }
      }
      this.fieldState.quizzes = quizzes;
    }
  };

  @computed
  get inputIsValid(): boolean {
    return this.props.item !== undefined || this.fieldState.name.getValid() !== undefined;
    /// FIXME: check consistency of groups and assessments
  }

  @computed
  get userQuizChangeList(): GroupUserQuizChange[] {
    return this.fieldState.quizzes.filter(uq => uq.kind === 'create' || uq.kind === 'update'); // || uq.kind === "delete" || uq.kind === "purge")
  }

  @computed
  get groupUserChangeList(): GroupUserChange[] {
    const { item: existing } = this.props;
    /// Strategy:
    /// If an item is still 'loaded' - ignore
    /// If it is 'create' - check in case it was loaded for safety - and for each not-undefined item, add create
    /// If it is 'update' - cf against original: defined -> undefined => delete, undefined -> defined => create
    const changes: GroupUserChange[] = this.fieldState.assignedUsers.filter(
      ug => ug.kind === 'create' || ug.kind === 'update'
    );
    const creates: GroupUserChange[] = changes.reduce((acc: GroupUserChange[], next) => {
      if (existing) {
        const existingMember = existing.members.find(m => m.value.appUserId === next.userId);
        const existingManager = existing.managers.find(m => m.value.appUserId === next.userId);
        if (existingMember === undefined || existingManager === undefined) {
          acc.push({
            kind: 'create',
            userId: next.userId,
            member: existingMember === undefined ? next.member : undefined,
            manager: existingManager === undefined ? next.manager : undefined
          });
        }
      } else {
        acc.push({
          kind: 'create',
          userId: next.userId,
          member: next.member,
          manager: next.manager
        });
      }
      return acc;
    }, []);
    const deletes: GroupUserChange[] = changes.reduce((acc: GroupUserChange[], next) => {
      if (existing) {
        const existingMember = existing.members.find(m => m.value.appUserId === next.userId);
        const existingManager = existing.managers.find(m => m.value.appUserId === next.userId);
        if (next.member === undefined || next.manager === undefined) {
          acc.push({
            kind: 'delete',
            userId: next.userId,
            member: existingMember && next.member === undefined ? existingMember.id : undefined,
            manager: existingManager && next.manager === undefined ? existingManager.id : undefined
          });
        }
      }
      return acc;
    }, []);
    return [...creates, ...deletes];
  }

  onSave = async () => {
    const { item: existingGroup, onCreate, onUpdate, data } = this.props;

    runInAction(() => this.lockUpdates());

    /// Build from scratch here to get latest changes
    const group = makeUserGroup({
      appClientId: data.appClient.id,
      name: this.fieldState.name.value(),
      isGlobal: this.fieldState.isGlobal
    });

    // Because of UG members and managers, we need the group ID here
    if (existingGroup && onUpdate) {
      await onUpdate(
        makeWithDbId({
          id: existingGroup.id,
          value: { ...group }
        }),
        this.groupUserChangeList,
        this.userQuizChangeList
      );
    } else {
      await onCreate(group, this.groupUserChangeList, this.userQuizChangeList);
    }
  };

  Edit = () => (
    <React.Fragment>
      <h4>User Group</h4>
      <div className={styles.row}>
        <this.NameObserver />
      </div>
      <div className={classnames(styles.row, styles.checkItemSet)}>
        <this.GlobalObserver />
      </div>
    </React.Fragment>
  );

  render() {
    return (
      // {/* TODO aria (or DOM) labels */}
      <div className={styles.page}>
        <div className={styles.body}>
          <div>
            {renderButtons(
              this.onSave,
              undefined,
              this.props.onCancel,
              () => this.inputIsValid && !this.locked,
              undefined,
              () => !this.locked
            )}
          </div>
          <this.Edit />
        </div>
        <div className={styles.body}>
          <h4>{this.props.item && this.props.item.name} Group Users</h4>
          <div className={styles.row}>
            <this.AssignedUserListObserver />            
          </div>
          <div className={styles.row}>
            <this.UnassignedUserListObserver />            
          </div>
        </div>
        <div className={styles.body}>
          <h4>{this.props.item && this.props.item.name} Group Assessments</h4>
          <div className={styles.row}>
            <this.UserQuizListSummaryObserver />
          </div>
          <div className={styles.row}>
            <this.QuizListObserver />
          </div>
        </div>
      </div>
    );
  }
}

export interface GroupDeleterProps {
  items?: WithDbId<UserGroup>[];
  onDelete?(ids: DbKey<UserGroup>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to delete UserGroup */
// @observer
export class GroupDeleter extends React.Component<GroupDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Users', items.map(item => item.value.name))}
          </div>
        </div>
      </div>
    ) : null;
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/usergroup-editor/usergroup-editor.tsx